// src/services/cloudinary.js

// ─── Replace with your Cloudinary credentials ─────────────────────────────────
const CLOUD_NAME   = "dd8jq5lsu";
const UPLOAD_PRESET = "tripuraagro"; // create in Cloudinary dashboard
// ──────────────────────────────────────────────────────────────────────────────

const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

// ─── Compression settings ───────────────────────────────────────────────────
const MAX_WIDTH   = 1280;   // px — resize anything wider than this
const MAX_HEIGHT  = 1280;   // px
const JPEG_QUALITY = 0.7;   // 0–1, lower = smaller file, less quality
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Compress an image file in the browser before upload.
 * Resizes to fit within MAX_WIDTH x MAX_HEIGHT and re-encodes as JPEG
 * at JPEG_QUALITY. Falls back to the original file if compression fails
 * (e.g. non-image file, or an old browser without canvas support).
 * @param {File} file
 * @returns {Promise<File>}
 */
const compressImage = (file) => {
  return new Promise((resolve) => {
    // Skip compression for non-images (shouldn't normally happen here)
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Scale down proportionally if bigger than the max dimensions
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file); // fallback: compression failed, use original
            return;
          }
          // Keep original name but force .jpg since we're re-encoding as JPEG
          const newName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
          const compressedFile = new File([blob], newName, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file); // fallback: couldn't load, use original
    };

    img.src = objectUrl;
  });
};

/**
 * Upload a single file to Cloudinary (compresses first).
 * @param {File} file
 * @param {string} folder  e.g. "products", "banners", "blogs", "certificates"
 * @returns {Promise<{url: string, publicId: string}>}
 */
export const uploadImage = async (file, folder = "products") => {
  const compressed = await compressImage(file);

  const formData = new FormData();
  formData.append("file", compressed);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", `agro-ecommerce/${folder}`);

  const res = await fetch(CLOUDINARY_URL, { method: "POST", body: formData });
  if (!res.ok) throw new Error("Cloudinary upload failed");
  const data = await res.json();
  return { url: data.secure_url, publicId: data.public_id };
};

/**
 * Upload multiple files to Cloudinary in parallel (each compressed first).
 * @param {File[]} files
 * @param {string} folder
 * @returns {Promise<Array<{url: string, publicId: string}>>}
 */
export const uploadMultipleImages = async (files, folder = "products") => {
  return Promise.all(files.map((f) => uploadImage(f, folder)));
};

/**
 * Delete an image from Cloudinary via your own backend/Firebase Function.
 * For client-side only apps, deletion must go through a server.
 * This is a placeholder — implement a Firebase Cloud Function for production.
 */
export const deleteImage = async (publicId) => {
  console.warn("deleteImage: implement via Firebase Cloud Function", publicId);
};